import { apiClient } from "@/lib/api";
import type { StandardResponse } from "@/types/api-response";
import { extractData } from "@/types/api-response";

/** Slugs the backend accepts; mirrors AgreementRegistry.Slugs. */
export const AGREEMENT_TYPES = [
  "signup_commitment",
  "vm_creation_commitment",
] as const;
export type AgreementType = (typeof AGREEMENT_TYPES)[number];

export const AGREEMENT_TYPE_LABELS: Record<AgreementType, string> = {
  signup_commitment: "After sign-up",
  vm_creation_commitment: "Before creating a machine",
};

export const AGREEMENT_TYPE_HINTS: Record<AgreementType, string> = {
  signup_commitment:
    "Shown the first time a tenant opens the console. They cannot use it until they accept.",
  vm_creation_commitment:
    "Shown before the first machine is ordered. Enforced in the API, not only in the UI.",
};

/** Supported languages, in the order the editor shows them. The first is mandatory. */
export const AGREEMENT_CULTURES = ["vi", "en"] as const;
export type AgreementCulture = (typeof AGREEMENT_CULTURES)[number];

export const AGREEMENT_CULTURE_LABELS: Record<string, string> = {
  vi: "Tiếng Việt",
  en: "English",
};

export interface AgreementTranslationDto {
  culture: string;
  title: string;
  /** Already reduced to the supported tags by the server, so this is what customers read. */
  bodyHtml: string;
}

export interface AgreementRevisionDto {
  id: string;
  type: AgreementType;
  version: number;
  /** Oldest revision that still counts as agreed. Lower than `version` for a correction. */
  minAcceptedVersion: number;
  /** Every language this revision was published in; "vi" is always present. */
  translations: AgreementTranslationDto[];
  /** Covers every language, so it fingerprints the revision rather than one rendering. */
  contentHash: string;
  isActive: boolean;
  /** True for the revision customers are being shown — the highest version of its commitment. */
  isCurrent: boolean;
  publishedAt: string;
  /** Publish responses only: true when unsupported markup was stripped before saving. */
  contentWasReduced?: boolean;
}

export interface PublishAgreementRequest {
  type: AgreementType;
  /**
   * One entry per language. "vi" is mandatory — everything downstream falls back to it, so a
   * revision without it would leave a customer with an empty dialog and no way past the gate.
   */
  translations: AgreementTranslationDto[];
  /** False publishes a correction: a new revision that does not ask agreed people again. */
  requireReacceptance: boolean;
}

export const adminAgreementService = {
  async getAgreements(): Promise<AgreementRevisionDto[]> {
    const response =
      await apiClient.get<StandardResponse<AgreementRevisionDto[]>>(
        "/api/v1/admin/agreements",
      );
    return extractData(response.data);
  },

  async publish(
    data: PublishAgreementRequest,
  ): Promise<AgreementRevisionDto> {
    const response = await apiClient.post<StandardResponse<AgreementRevisionDto>>(
      "/api/v1/admin/agreements",
      data,
    );
    return extractData(response.data);
  },

  async setActive(
    type: AgreementType,
    isActive: boolean,
  ): Promise<AgreementRevisionDto> {
    const response = await apiClient.put<StandardResponse<AgreementRevisionDto>>(
      "/api/v1/admin/agreements/active",
      { type, isActive },
    );
    return extractData(response.data);
  },
};
